import { Component, computed, inject, input, signal } from '@angular/core';

import type { Attachment } from '@core/attachments-types';
import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { I18nService } from '@core/i18n-legacy';
import type { InterviewResultAnswerOption } from '@core/interview-results-types';
import { QuestionKind } from '@core/interview-templates-types';
import {
  type FeatureTooltipDirectiveConfig,
  FeatureTooltipWrapperDirective,
} from '@core/product-updates-legacy';
import { TellentBrand } from '@core/tellent-types';
import { RemoveHTMLTagsPipe } from '@core/user-interface-legacy';
import { ComponentPositionPair, ComponentTooltipPosition } from '@core/user-interface-types';
import {
  EvaluationCommentTooltipService,
  EvaluationFieldRefDirective,
  EvaluationFormSubmissionService,
  TemplateEvaluationFormService,
} from '@recruitee/evaluations-logic';
import type { EvaluationAnswerForm, EvaluationChoiceOption } from '@recruitee/evaluations-types';
import {
  EvaluationBooleanFieldComponent,
  EvaluationDateFieldComponent,
  EvaluationDropdownFieldComponent,
  EvaluationFormDescriptionComponent,
  EvaluationSalaryFieldComponent,
  EvaluationInfoFieldComponent,
  EvaluationSectionFieldComponent,
  EvaluationMultiChoiceFieldComponent,
  EvaluationQuestionCardComponent,
  EvaluationScorecardFieldComponent,
  EvaluationSingleChoiceFieldComponent,
  EvaluationSingleLineFieldComponent,
  EvaluationSkillsFieldComponent,
} from '@recruitee/evaluations-ui';

import { EvaluationCommentFieldComponent } from '../../common/evaluation-comment-field/evaluation-comment-field.component';
import { EvaluationRatingAdvancedFieldComponent } from '../../common/evaluation-rating-advanced-field/evaluation-rating-advanced-field.component';
import { EvaluationTextAnswerFieldComponent } from '../../common/evaluation-text-answer-field/evaluation-text-answer-field.component';
import { EvaluationFileFieldComponent } from '../evaluation-file-field/evaluation-file-field.component';

@Component({
  selector: 'rt-template-evaluation-form',
  templateUrl: './template-evaluation-form.component.html',
  imports: [
    RemoveHTMLTagsPipe,
    EvaluationFieldRefDirective,
    EvaluationFormDescriptionComponent,
    EvaluationSingleLineFieldComponent,
    EvaluationScorecardFieldComponent,
    EvaluationBooleanFieldComponent,
    EvaluationSingleChoiceFieldComponent,
    EvaluationMultiChoiceFieldComponent,
    EvaluationDropdownFieldComponent,
    EvaluationDateFieldComponent,
    EvaluationSalaryFieldComponent,
    EvaluationInfoFieldComponent,
    EvaluationSectionFieldComponent,
    EvaluationFileFieldComponent,
    EvaluationSkillsFieldComponent,
    EvaluationRatingAdvancedFieldComponent,
    EvaluationTextAnswerFieldComponent,
    EvaluationQuestionCardComponent,
    EvaluationCommentFieldComponent,
    FeatureTooltipWrapperDirective,
  ],
})
export class TemplateEvaluationFormComponent {
  private readonly formService = inject(TemplateEvaluationFormService);
  private readonly formSubmissionService = inject(EvaluationFormSubmissionService);
  private readonly commentTooltip = inject(EvaluationCommentTooltipService);
  private readonly i18n = inject(I18nService);
  private readonly expandedComments = signal(new Set<number>());
  private readonly discoveryTooltipAnswerId = signal<number | null>(null);
  private readonly commentTooltipDismissed = signal(false);

  protected readonly QuestionKind = QuestionKind;
  protected readonly form = this.formService.form;
  protected readonly submissionAttempted = this.formSubmissionService.submissionAttempted;
  protected readonly useProsemirror = injectDevFlag(DevFlag.ProsemirrorInEvaluations);
  protected readonly commentDiscoveryActive = computed(
    () => this.useProsemirror && !this.commentTooltip.used() && !this.commentTooltipDismissed(),
  );
  protected readonly commentTooltipConfig: FeatureTooltipDirectiveConfig = {
    id: 'evaluation_comment_tooltip',
    flagName: 'evaluationCommentTooltipUsed',
    isNew: true,
    brand: TellentBrand.Tellent,
    title: this.i18n.translate('app.evaluation_panel.comment_tooltip.title'),
    description: this.i18n.translate('app.evaluation_panel.comment_tooltip.description'),
    ctaText: this.i18n.translate('app.common.got_it'),
    withPush: true,
    positions: {
      [ComponentTooltipPosition.Left]: new ComponentPositionPair(
        { originX: 'start', originY: 'top' },
        { overlayX: 'end', overlayY: 'top' },
        'topright',
        -20,
        -18,
      ),
    },
  };

  public readonly infoTitle = input<string>('');
  public readonly infoDescription = input<string>('');
  public readonly filePreview = input<boolean>(false);
  public readonly withPadding = input<boolean>(true);
  public readonly ratingScale = input<RatingScale | null>(null);
  public readonly attachmentsByAnswerId = input<Map<number, Attachment[]>>(
    new Map<number, Attachment[]>(),
  );
  public readonly commentTooltipOpen = computed(
    () => this.commentDiscoveryActive() && this.discoveryTooltipAnswerId() !== null,
  );

  protected toChoiceOptions(options: InterviewResultAnswerOption[]): EvaluationChoiceOption[] {
    return options.map(option => ({ value: option.title, label: option.title }));
  }

  protected currencyLabel(code: string | null): string {
    return this.formService.currencyLabel(code);
  }

  protected isCommentExpanded(answer: EvaluationAnswerForm): boolean {
    return (
      this.expandedComments().has(answer.controls.id.value) ||
      !!answer.controls.note.value ||
      !!answer.controls.noteJson.value
    );
  }

  protected toggleComment(answer: EvaluationAnswerForm): void {
    this.expandedComments.update(ids => new Set(ids).add(answer.controls.id.value));
  }

  protected onCommentScrolled(answer: EvaluationAnswerForm): void {
    if (this.commentDiscoveryActive() && this.discoveryTooltipAnswerId() === null) {
      this.discoveryTooltipAnswerId.set(answer.controls.id.value);
    }
  }

  protected onCommentTooltipDismissed(): void {
    this.commentTooltipDismissed.set(true);
  }

  protected isCommentTooltipOpen(answer: EvaluationAnswerForm): boolean {
    return (
      this.commentDiscoveryActive() && this.discoveryTooltipAnswerId() === answer.controls.id.value
    );
  }

  protected wasCommentExpandedByUser(answer: EvaluationAnswerForm): boolean {
    return this.expandedComments().has(answer.controls.id.value);
  }
}
